import React from 'react';
import { ArrowRight } from 'lucide-react';
import type { Rapprochement } from '../../../lib/planningExcelImport/types';
import { bilanRapprochement, etiquetteNiveau, nomAffiche, type SalarieIAmani } from '../../../lib/planningExcelImport/rapprocherSalaries';

/**
 * Étape 2 de l'assistant d'import (maquette 26b) — reconnaître les salariés.
 *
 * Un nom non reconnu n'est jamais deviné : ses lignes sont laissées de côté, et il n'est pas créé comme
 * nouveau salarié. Une correspondance sur le seul nom de famille est proposée décochée : c'est à
 * l'utilisateur de la confirmer. Ce qu'il confirme ici est mémorisé et réappliqué aux imports suivants.
 */
const TONS = {
    exact: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
    mots: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
    alias: 'bg-state-ok-soft border-state-ok/30 text-state-ok',
    aConfirmer: 'bg-state-warn-soft border-state-warn/30 text-state-warn',
    inconnu: 'bg-state-danger-soft border-state-danger/30 text-state-danger',
} as const;

export function Etape2Salaries({ rapprochements, salaries, onBasculer, onAssocier }: {
    rapprochements: Rapprochement[];
    salaries: SalarieIAmani[];
    onBasculer: (nomFichier: string) => void;
    onAssocier: (nomFichier: string, employeeId: string) => void;
}) {
    const bilan = bilanRapprochement(rapprochements);
    const tuiles = [
        { n: bilan.reconnus, label: 'reconnus sans hésitation', classe: 'bg-state-ok-soft border-state-ok/30 text-state-ok' },
        { n: bilan.aConfirmer, label: 'à confirmer', classe: 'bg-state-warn-soft border-state-warn/30 text-state-warn' },
        { n: bilan.inconnus, label: 'inconnus, ignorés', classe: 'bg-state-danger-soft border-state-danger/30 text-state-danger' },
    ];

    return (
        <div className="flex flex-col gap-[13px]">
            <div className="grid grid-cols-3 gap-2">
                {tuiles.map((tuile) => (
                    <div key={tuile.label} className={`px-3 py-[11px] rounded-[10px] border ${tuile.classe}`}>
                        <div className="text-[21px] font-bold tracking-tight tabular-nums leading-none">{tuile.n}</div>
                        <div className="mt-[5px] text-[11px] leading-snug text-pretty">{tuile.label}</div>
                    </div>
                ))}
            </div>

            <div className="border border-border rounded-[10px] overflow-hidden">
                <div className="grid grid-cols-[minmax(0,1fr)_18px_minmax(0,1fr)] gap-[9px] px-[13px] py-2 bg-surface-soft border-b border-border">
                    <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">Dans le fichier</span>
                    <span />
                    <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">Dans l'IAmani</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                    {rapprochements.map((rapprochement) => (
                        <div
                            key={`${rapprochement.nomFichier}-${rapprochement.ligneExcel}`}
                            className="grid grid-cols-[minmax(0,1fr)_18px_minmax(0,1fr)] items-center gap-[9px] px-[13px] py-[9px] border-b border-border/60 last:border-b-0"
                        >
                            <span className="text-xs min-w-0 truncate text-content" title={rapprochement.nomFichier}>
                                {rapprochement.nomFichier.trim() || '—'}
                            </span>
                            <ArrowRight size={12} className="shrink-0 text-content-muted" />
                            {rapprochement.niveau === 'inconnu' ? (
                                <select
                                    value=""
                                    onChange={(e) => e.target.value && onAssocier(rapprochement.nomFichier, e.target.value)}
                                    className={`min-w-0 w-full px-[9px] py-1.5 rounded-[7px] border text-xs font-semibold ${TONS.inconnu}`}
                                >
                                    <option value="">aucun salarié — ligne ignorée</option>
                                    {salaries.map((salarie) => (
                                        <option key={salarie.id} value={salarie.id}>{nomAffiche(salarie)}</option>
                                    ))}
                                </select>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => onBasculer(rapprochement.nomFichier)}
                                    title={rapprochement.retenu ? 'Écarter cette ligne' : 'Retenir ce rapprochement'}
                                    className={`flex items-center justify-between gap-[7px] min-w-0 px-[9px] py-1.5 rounded-[7px] border text-left ${TONS[rapprochement.niveau]} ${rapprochement.retenu ? '' : 'opacity-45 line-through'}`}
                                >
                                    <span className="text-xs font-semibold min-w-0 truncate">{rapprochement.employeeNom}</span>
                                    <span className="text-[10px] font-bold whitespace-nowrap shrink-0 opacity-75">{etiquetteNiveau(rapprochement)}</span>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <p className="text-xs leading-relaxed text-content-muted text-pretty">
                Un nom non reconnu n'est jamais deviné : ses lignes sont laissées de côté. Le rapprochement confirmé
                ici sert aux imports suivants.
            </p>
        </div>
    );
}
